import { MedicalItemsTab } from "@/Common/Models/Patient/PatientEnums";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { MedicalItem } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemScreeningTestResponse } from "@/Patient/Models/MedicalSummary/Response/MedicalItemScreeningTestResponse";
import { ConsultationRequestsInboxItem, RequestsInboxItem } from "@/Portfolio/Models/MyNotesInboxItem";

import styles from "./PendingItemsSection.module.css";

interface IPendingItemsSectionProps {
    labRequests: RequestsInboxItem[];
    consultationRequests: ConsultationRequestsInboxItem[];
    preventiveActivities: MedicalItemScreeningTestResponse[];
    onLabRequestItemClick: (model: RequestsInboxItem, view: MedicalItemsTab) => void;
    onConsultationRequestItemClick: (model: ConsultationRequestsInboxItem, view: MedicalItemsTab) => void;
    onPreventiveActivitytemClick: (model: MedicalItem, view: MedicalItemsTab) => void;
}

interface IPendingItemData {
    id: string;
    item: string;
    reason: string;
    dueDate: Date;
    type: PendingItemType;
    model: RequestsInboxItem | ConsultationRequestsInboxItem | MedicalItem;
}

enum PendingItemType {
    PreventiveActivity,
    LabRequest,
    ConsultationRequest
}

function PendingItemsSection(props: IPendingItemsSectionProps): JSX.Element {
    const onRowClick = (type: PendingItemType, model: RequestsInboxItem | ConsultationRequestsInboxItem | MedicalItem): void => {
        switch (type) {
            case PendingItemType.LabRequest:
                props.onLabRequestItemClick(model as RequestsInboxItem, MedicalItemsTab.PendingItems);
                break;
            case PendingItemType.ConsultationRequest:
                props.onConsultationRequestItemClick(model as ConsultationRequestsInboxItem, MedicalItemsTab.PendingItems);
                break;
            case PendingItemType.PreventiveActivity:
                props.onPreventiveActivitytemClick(model as MedicalItem, MedicalItemsTab.PendingItems);
                break;
        }
    };

    const dataset = buildPendingItemsDataSet(props.labRequests, props.consultationRequests, props.preventiveActivities);

    return (
        <>
            <table className={styles.pendingItemsTable}>
                <thead>
                    <tr>
                        <th className={styles.item}>
                            {cacheStore.GetString("ItemLabel")}
                        </th>
                        <th className={styles.reason}>
                            {cacheStore.GetString("ReasonLabel")}
                        </th>
                        <th className={styles.date}>
                            {cacheStore.GetString("DueDateLabel")}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {dataset.map(item => (
                        <tr key={"pendingItem" + item.id} onClick={() => onRowClick(item.type, item.model)}>
                            <td className={styles.item}>
                                {item.item}
                            </td>
                            <td className={styles.reason}>
                                {item.reason}
                            </td>
                            <td className={styles.date}>
                                {!item.dueDate ? "" : LuxonDateTimeParser.parse(item.dueDate).toFormat("yy/MM/dd")}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
}

const buildPendingItemsDataSet = (
    labRequests: RequestsInboxItem[],
    consultationRequests: ConsultationRequestsInboxItem[],
    preventiveActivities: MedicalItemScreeningTestResponse[]
): IPendingItemData[] => {
    const pendingItems: IPendingItemData[] = [];
    pendingItems.push(...(labRequests.map(request => ({
        id: request.RequestId,
        item: request.Request,
        reason: request.Reason,
        dueDate: request.DueDate,
        type: PendingItemType.LabRequest,
        model: request
    }))));
    pendingItems.push(...(consultationRequests.map(consultationRequest => ({
        id: consultationRequest.ConsultationRequestId,
        item: consultationRequest.Speciality,
        reason: consultationRequest.Reason,
        dueDate: consultationRequest.DueDate,
        type: PendingItemType.ConsultationRequest,
        model: consultationRequest
    }))));
    pendingItems.push(...(preventiveActivities.map(preventiveActivity => ({
        id: preventiveActivity.Id,
        item: preventiveActivity.ScreeningTestDefinitionId ?
            cacheStore.getTestDefinitionById(preventiveActivity.ScreeningTestDefinitionId).Name : "",
        reason: "",
        dueDate: preventiveActivity.DueDate,
        type: PendingItemType.PreventiveActivity,
        model: preventiveActivity
    }))));

    return pendingItems;
};

export { PendingItemsSection };
